import React from 'react'
import { Company } from '../../api/chatService'
import {
  ANALYTICS_GAILS_PERMISSION_ID,
  ANALYTICS_TBF_PERMISSION_ID,
  hasPermission,
} from '../../constants/permissions'

interface WelcomeProps {
  onExampleClick: (example: string) => void
  company: Company | null
  permissionIds: number[] | undefined
}

// Every example here was run against BreadGPT several times and its numbers
// checked against the data model, for the users who are shown it. Re-check any
// new one before adding it:
// - Ask only for what the model actually holds. The Gail's sales models have no
//   product data (DimProduct[IsActive] is TRUE for every product), and hold only
//   recent days: DPR has the last ~8 days, WOW has today plus the weeks before.
// - Give exports a fixed size (e.g. "top 50"). An open-ended export varied from
//   run to run, once including every customer with no sales.
// - Avoid "this month": it is empty on the 1st.
const EXAMPLES: Record<Company, { everyone: string[]; analytics: string[] }> = {
  gails: {
    everyone: [
      "What's the recipe for soho buns?",
      'What is our sick leave policy?',
      'How does the Staff Surplus till button work?',
      'How do I log a call-out on ProNett?',
    ],
    // Analytics answers need the company's analytics permission; anyone else is
    // told they don't have access, so these are only offered to those who do.
    analytics: [
      "Export yesterday's sales by bakery as a CSV",
      'Show a chart of our top 10 bakeries by sales last week',
    ],
  },
  tbf: {
    everyone: [
      'How do I match an invoice in Tropos?',
      "The MK2 isn't printing, what should I check?",
      'What is the holiday policy?',
      'How do I create a sales order in Tropos?',
    ],
    analytics: [
      'Export our top 50 customers by sales last week as a CSV',
      'Show a chart of weekly cases sold over the last 8 weeks',
    ],
  },
}

const ANALYTICS_PERMISSION: Record<Company, number> = {
  gails: ANALYTICS_GAILS_PERMISSION_ID,
  tbf: ANALYTICS_TBF_PERMISSION_ID,
}

const EXAMPLE_COUNT = 4

/** The examples to offer: two of the company's general ones, then its analytics
 * ones if the user may run them; otherwise just its general ones. */
export const examplesFor = (
  company: Company | null,
  permissionIds: number[] | undefined,
): string[] => {
  const { everyone, analytics } = EXAMPLES[company ?? 'gails']
  if (!hasPermission(permissionIds, ANALYTICS_PERMISSION[company ?? 'gails'])) {
    return everyone.slice(0, EXAMPLE_COUNT)
  }
  return [...everyone.slice(0, EXAMPLE_COUNT - analytics.length), ...analytics]
}

const Welcome: React.FC<WelcomeProps> = ({
  onExampleClick,
  company,
  permissionIds,
}) => {
  return (
    <div className="chat-page__welcome">
      <h2 className="chat-page__welcome-title">How can I help you today?</h2>
      <p className="chat-page__welcome-subtitle">
        I'm BreadGPT, an AI assistant for internal knowledge and analytics.
      </p>

      <div className="chat-page__welcome-examples">
        {examplesFor(company, permissionIds).map((example) => (
          <div
            key={example}
            className="chat-page__welcome-example"
            onClick={() => onExampleClick(example)}
          >
            {example}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Welcome
